/**
 * Ontology Management - Settings Page
 *
 * Central hub for managing organization-specific private ontologies.
 * Provides list view, upload, browse, and lifecycle management.
 *
 * @see Documentation/Development/Design Docs/Sprint 8/Private_Ontology_Feature_Design_v1.1_INTEGRATED.md
 * @version 1.0.0
 * @date 2025-11-28
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, BookOpen } from 'lucide-react';
import { useApiClient } from '../../hooks/useApiClient';
import { UploadWizard } from '../../components/ontology/UploadWizard';
import { OntologyBrowser } from '../../components/ontology/OntologyBrowser';
import '../../styles/ontology-management.css';

// =============================================================================
// TYPES
// =============================================================================

export type OntologyStatus = 'active' | 'deprecated' | 'draft';
export type OntologyFormat = 'owl' | 'turtle' | 'jsonld' | 'rdfxml';

export interface OntologyStatistics {
  term_count: number;
  class_count: number;
  property_count: number;
  individual_count: number;
  hierarchy_depth: number;
  max_breadth: number;
  triple_count: number;
  namespace_count: number;
}

export interface OntologySummary {
  ontology_id: string;
  name: string;
  namespace: string;
  version: string;
  format: OntologyFormat;
  description?: string;
  status: OntologyStatus;
  access_level: 'organization' | 'private' | 'shared';
  statistics?: OntologyStatistics;
  created_at: string;
  updated_at?: string;
  deployed_at?: string;
}

interface ListOntologiesResponse {
  ontologies: OntologySummary[];
  total: number;
  limit: number;
  offset: number;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

function getStatusColor(status: OntologyStatus): string {
  switch (status) {
    case 'active':
      return 'status-active';
    case 'deprecated':
      return 'status-deprecated';
    case 'draft':
    default:
      return 'status-draft';
  }
}

function getStatusLabel(status: OntologyStatus): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'deprecated':
      return 'Deprecated';
    case 'draft':
    default:
      return 'Draft';
  }
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

// =============================================================================
// ONTOLOGY CARD COMPONENT
// =============================================================================

interface OntologyCardProps {
  ontology: OntologySummary;
  onView: (id: string) => void;
  onDeploy: (id: string) => void;
  onDeprecate: (id: string) => void;
}

const OntologyCard: React.FC<OntologyCardProps> = ({
  ontology,
  onView,
  onDeploy,
  onDeprecate,
}) => {
  const [showActions, setShowActions] = useState(false);

  return (
    <div className="ontology-card">
      <div className="ontology-card-header">
        <div className="ontology-info">
          <h3 className="ontology-name">{ontology.name}</h3>
          <span className="ontology-namespace">
            {ontology.namespace}:{ontology.version}
          </span>
        </div>
        <div className="ontology-status-badge">
          <span className={`status-badge ${getStatusColor(ontology.status)}`}>
            {getStatusLabel(ontology.status)}
          </span>
        </div>
      </div>

      {ontology.description && (
        <p className="ontology-description">{ontology.description}</p>
      )}

      <div className="ontology-stats">
        {ontology.statistics && (
          <>
            <div className="stat-item">
              <span className="onto-stat-num">
                {formatNumber(ontology.statistics.term_count)}
              </span>
              <span className="stat-label">Terms</span>
            </div>
            <div className="stat-item">
              <span className="onto-stat-num">
                {formatNumber(ontology.statistics.class_count)}
              </span>
              <span className="stat-label">Classes</span>
            </div>
            <div className="stat-item">
              <span className="onto-stat-num">
                {formatNumber(ontology.statistics.property_count)}
              </span>
              <span className="stat-label">Properties</span>
            </div>
            <div className="stat-item">
              <span className="onto-stat-num">{ontology.statistics.hierarchy_depth}</span>
              <span className="stat-label">Depth</span>
            </div>
          </>
        )}
      </div>

      <div className="ontology-metadata">
        <span className="metadata-item">
          Format: <strong>{ontology.format.toUpperCase()}</strong>
        </span>
        <span className="metadata-item">
          Created: <strong>{formatDate(ontology.created_at)}</strong>
        </span>
        {ontology.deployed_at && (
          <span className="metadata-item">
            Deployed: <strong>{formatDate(ontology.deployed_at)}</strong>
          </span>
        )}
      </div>

      <div className="ontology-actions">
        <button className="btn btn-primary" onClick={() => onView(ontology.ontology_id)}>
          Browse Terms
        </button>

        <div className="action-menu-container">
          <button
            className="btn btn-secondary btn-icon"
            onClick={() => setShowActions(!showActions)}
            aria-label="More actions"
          >
            ⋮
          </button>

          {showActions && (
            <div className="action-menu">
              {ontology.status === 'draft' && (
                <button
                  className="action-menu-item"
                  onClick={() => {
                    setShowActions(false);
                    onDeploy(ontology.ontology_id);
                  }}
                >
                  Deploy Ontology
                </button>
              )}
              {ontology.status !== 'deprecated' && (
                <button
                  className="action-menu-item action-danger"
                  onClick={() => {
                    setShowActions(false);
                    onDeprecate(ontology.ontology_id);
                  }}
                >
                  Deprecate
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const OntologyManagement: React.FC = () => {
  const { authFetch } = useApiClient();
  // State
  const [ontologies, setOntologies] = useState<OntologySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<OntologyStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadWizard, setShowUploadWizard] = useState(false);
  const [selectedOntology, setSelectedOntology] = useState<string | null>(null);
  const [showBrowser, setShowBrowser] = useState(false);

  // Fetch ontologies
  const fetchOntologies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await authFetch(`/api/ontologies?${params}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch ontologies: ${response.statusText}`);
      }

      const data: ListOntologiesResponse = await response.json();
      setOntologies(data.ontologies);
    } catch (err: unknown) {
      console.error('[OntologyManagement] Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch ontologies');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable from useApiClient
  }, [statusFilter]);

  useEffect(() => {
    fetchOntologies();
  }, [fetchOntologies]);

  // Handlers
  const handleUploadComplete = (_ontologyId: string) => {
    setShowUploadWizard(false);
    fetchOntologies();
  };

  const handleViewOntology = (ontologyId: string) => {
    setSelectedOntology(ontologyId);
    setShowBrowser(true);
  };

  const handleDeployOntology = async (ontologyId: string) => {
    if (!confirm('Deploy this ontology? It will become active for validation.')) {
      return;
    }

    try {
      const response = await authFetch(`/api/ontologies/${ontologyId}/deploy`, {
        method: 'PATCH',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to deploy');
      }

      fetchOntologies();
    } catch (err: unknown) {
      console.error('[OntologyManagement] Deploy error:', err);
      alert(`Failed to deploy ontology: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleDeprecateOntology = async (ontologyId: string) => {
    const reason = prompt('Enter deprecation reason:');
    if (!reason) return;

    try {
      const response = await authFetch(`/api/ontologies/${ontologyId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          deprecation_reason: reason,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to deprecate');
      }

      fetchOntologies();
    } catch (err: unknown) {
      console.error('[OntologyManagement] Deprecate error:', err);
      alert(`Failed to deprecate ontology: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Filter ontologies by search query
  const filteredOntologies = ontologies.filter(
    (o) =>
      o.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.namespace.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Count by status
  const statusCounts = {
    all: ontologies.length,
    active: ontologies.filter((o) => o.status === 'active').length,
    draft: ontologies.filter((o) => o.status === 'draft').length,
    deprecated: ontologies.filter((o) => o.status === 'deprecated').length,
  };

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <div className="ontology-management">
      {/* Header */}
      <div className="management-header">
        <div className="header-left">
          <h1>Ontology Management</h1>
          <p className="subtitle">
            Upload, browse, and manage your organization's private ontologies for
            enterprise validation
          </p>
        </div>
        <div className="header-right">
          <button
            className="btn btn-primary btn-lg"
            onClick={() => setShowUploadWizard(true)}
          >
            <span className="icon">+</span> Upload Ontology
          </button>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="status-tabs">
        <button
          className={`status-tab ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          All ({statusCounts.all})
        </button>
        <button
          className={`status-tab ${statusFilter === 'active' ? 'active' : ''}`}
          onClick={() => setStatusFilter('active')}
        >
          Active ({statusCounts.active})
        </button>
        <button
          className={`status-tab ${statusFilter === 'draft' ? 'active' : ''}`}
          onClick={() => setStatusFilter('draft')}
        >
          Draft ({statusCounts.draft})
        </button>
        <button
          className={`status-tab ${statusFilter === 'deprecated' ? 'active' : ''}`}
          onClick={() => setStatusFilter('deprecated')}
        >
          Deprecated ({statusCounts.deprecated})
        </button>
      </div>

      {/* Search */}
      <div className="search-section">
        <input
          type="text"
          className="search-input"
          placeholder="Search by name or namespace..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="results-count">
          {filteredOntologies.length} of {ontologies.length} ontologies
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading ontologies...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-state">
          <div className="error-icon">
            <AlertTriangle size={44} aria-hidden="true" />
          </div>
          <h3>Failed to Load Ontologies</h3>
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={fetchOntologies}>
            Retry
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && ontologies.length === 0 && (
        <div className="empty-state">
          <div className="empty-illustration">
            <BookOpen size={48} aria-hidden="true" />
          </div>
          <h2>No Ontologies Yet</h2>
          <p>
            Upload your organization's private ontologies to enable enterprise-level
            validation and semantic alignment across your data portfolio.
          </p>
          <button
            className="btn btn-primary btn-lg"
            onClick={() => setShowUploadWizard(true)}
          >
            Upload Your First Ontology
          </button>
        </div>
      )}

      {/* Ontology Grid */}
      {!loading && !error && filteredOntologies.length > 0 && (
        <div className="ontology-grid">
          {filteredOntologies.map((ontology) => (
            <OntologyCard
              key={ontology.ontology_id}
              ontology={ontology}
              onView={handleViewOntology}
              onDeploy={handleDeployOntology}
              onDeprecate={handleDeprecateOntology}
            />
          ))}
        </div>
      )}

      {/* No Results State */}
      {!loading &&
        !error &&
        ontologies.length > 0 &&
        filteredOntologies.length === 0 && (
          <div className="no-results-state">
            <p>No ontologies match your search: "{searchQuery}"</p>
            <button className="btn btn-secondary" onClick={() => setSearchQuery('')}>
              Clear Search
            </button>
          </div>
        )}

      {/* Upload Wizard Modal */}
      {showUploadWizard && (
        <UploadWizard
          isOpen={showUploadWizard}
          onClose={() => setShowUploadWizard(false)}
          onComplete={handleUploadComplete}
        />
      )}

      {/* Ontology Browser Modal */}
      {showBrowser && selectedOntology && (
        <OntologyBrowser
          ontologyId={selectedOntology}
          isOpen={showBrowser}
          onClose={() => {
            setShowBrowser(false);
            setSelectedOntology(null);
          }}
        />
      )}
    </div>
  );
};

export default OntologyManagement;
